import React, { useCallback, useEffect, useState } from "react";
import moment from "moment";
import { useSelector } from "react-redux";
import { postApi } from "../service";
import { Logout } from "../common/Funcation";
import { USER_API } from "../common/common";
import Loader from "../components/Loader";

const SPORT_OPTIONS = [
    { value: "cricket", label: "Cricket" },
    { value: "soccer", label: "Soccer" },
    { value: "tennis", label: "Tennis" },
];

type ResultItem = {
    _id: string;
    name: string;
    openDate: string;
    startDate: string;
    type: string;
    gameId: number;
    winner: string;
    result: {
        score1: string;
        score2: string;
    };
};

const CheckResultsMobile = () => {
    const DD = useSelector((e: any) => e.domainDetails);

    const [selectedDay, setSelectedDay] = useState<"today" | "yesterday">(
        "today"
    );

    const [sportType, setSportType] = useState("cricket");

    const [results, setResults] = useState<ResultItem[]>([]);

    const [isLoading, setIsLoading] = useState(false);

    const getDateRange = useCallback(() => {
        const today = new Date();

        if (selectedDay === "today") {
            const start = new Date(today);
            start.setHours(0, 0, 0, 0);

            const end = new Date(today);
            end.setHours(23, 59, 59, 999);

            return {
                fromDate: start.toISOString(),
                toDate: end.toISOString(),
            };
        }

        const yesterday = new Date(today);
        yesterday.setDate(yesterday.getDate() - 1);

        const start = new Date(yesterday);
        start.setHours(0, 0, 0, 0);

        const end = new Date(yesterday);
        end.setHours(23, 59, 59, 999);

        return {
            fromDate: start.toISOString(),
            toDate: end.toISOString(),
        };
    }, [selectedDay]);

    const fetchResults = useCallback(async () => {
        setIsLoading(true);

        const { fromDate, toDate } = getDateRange();

        const payload = {
            api: USER_API.MATCH_RESULT,
            value: {
                type: sportType,
                page: "1",
                limit: "50",
                fromDate,
                toDate,
            },
        };

        try {
            const response = await postApi(payload);

            const data = response?.data?.data;

            setResults(data?.results || []);
        } catch (error: any) {
            setResults([]);

            if (error?.response?.data?.statusCode === 401) {
                Logout();
            }
        } finally {
            setIsLoading(false);
        }
    }, [sportType, getDateRange]);

    useEffect(() => {
        fetchResults();
    }, [fetchResults]);

    const handleDayChange = (day: "today" | "yesterday") => {
        if (day === selectedDay) return;

        setSelectedDay(day);
    };

    const formatDate = (date: string) => {
        if (!date) return "-";

        return moment(date).format("YYYY-MM-DD HH:mm");
    };

    const formatName = (name: string) => {
        if (!name) return "-";

        return name;
    };

    return (
        <>
            <style>{`
        .result-page {
          width: 100%;
          min-height: calc(100vh - 49px);
          background: #edf6fa;
          box-sizing: border-box;
          font-family: Ubuntu, sans-serif;
        }

        .result-page *,
        .result-page *::before,
        .result-page *::after {
          box-sizing: border-box;
        }

        /* --------------------------------
           TOP FILTER
        -------------------------------- */

        .result-filter {
          width: 100%;
          display: flex;
          align-items: center;
          gap: 0.5rem;
          background: #fff;
          padding: 0.5rem;
          border-radius: 0.5rem;
        }

        .result-day-button {
          height: 32px;
          flex: 1;
          border: none;
          border-radius: 3px;
          background: transparent;
          color: #111;
          font-size: 13.52px;
          font-weight: 700;
          cursor: pointer;
          padding: 0 10px;
          font-family: Ubuntu, sans-serif;
        }

        .result-day-button.active {
          background: #ffc800;
        }

        .result-sport-select {
          width: 122px;
          height: 32px;
          border: none;
          border-radius: 0;
          background: #dedede;
          color: #111;
          padding: 0 7px;
          font-size: 13px;
          outline: none;
          cursor: pointer;
        }

        /* --------------------------------
           RESULT LIST
        -------------------------------- */

        .result-list {
          width: 100%;
          padding: 1rem;
        }

        .result-card {
          width: 100%;
          background: #fff;
          border-radius: 0 0 8px 8px;
          overflow: hidden;
          margin-bottom: 7px;
        }

        .result-row {
          width: 100%;
          min-height: 32px;
          display: grid;
          grid-template-columns: 126px minmax(0, 1fr);
          align-items: center;
          border-bottom: 1px solid #d4dfe4;
        }

        .result-row:last-child {
          border-bottom: none;
        }

        .result-label {
          padding: 7px 7px;
          color: #33495a;
          font-size: 12.48px;
          line-height: 13px;
          white-space: nowrap;
        }

        .result-value {
          min-width: 0;
          padding: 7px 6px;
          color: #62788a;
          font-size: 12.48px;
          font-weight: 600;
          line-height: 13px;
          overflow-wrap: anywhere;
        }

        .result-name {
          color: #60798d;
          font-weight: 600;
        }

        .result-winner {
          color: #60798d;
          font-weight: 700;
        }

        /* --------------------------------
           LOADER
        -------------------------------- */

        .result-loader {
          width: 100%;
          min-height: 250px;
          display: flex;
          align-items: center;
          justify-content: center;
        }

        /* --------------------------------
           EMPTY
        -------------------------------- */

        .result-empty {
          width: 100%;
          background: #fff;
          border-radius: 0 0 8px 8px;
          min-height: 70px;
          display: flex;
          align-items: center;
          justify-content: center;
          color: #687b87;
          font-size: 12px;
        }

        /* --------------------------------
           MOBILE
        -------------------------------- */

        @media (max-width: 576px) {
          .result-page {
          }

          .result-filter {
            gap: 0.5rem;
          }

          .result-day-button {
            font-size: 13.52px;
            font-weight: 700;
          }

          .result-sport-select {
            width: 122px;
            font-size: 14.56px;
          }

          .result-row {
            grid-template-columns: 126px minmax(0, 1fr);
          }
        }
      `}</style>

            <div className="result-page">

                {/* =========================
            DAY / SPORT FILTER
        ========================== */}
                <div className="result-filter">

                    <button
                        type="button"
                        className={`result-day-button ${selectedDay === "today" ? "active" : ""
                            }`}
                        onClick={() => handleDayChange("today")}
                    >
                        Today
                    </button>

                    <button
                        type="button"
                        className={`result-day-button ${selectedDay === "yesterday" ? "active" : ""
                            }`}
                        onClick={() => handleDayChange("yesterday")}
                    >
                        Yesterday
                    </button>

                    <select
                        className="result-sport-select"
                        value={sportType}
                        onChange={(e) => setSportType(e.target.value)}
                    >
                        {SPORT_OPTIONS.map((sport) => (
                            <option key={sport.value} value={sport.value}>
                                {sport.label}
                            </option>
                        ))}
                    </select>
                </div>

                {/* =========================
            RESULT DATA
        ========================== */}

                {isLoading ? (
                    <div className="result-loader">
                        <Loader />
                    </div>
                ) : results.length === 0 ? (
                    <div className="no-event-outer">
                        <div className="no-event">
                            <svg stroke="currentColor" fill="currentColor" strokeWidth="0" viewBox="0 0 24 24" color="lightgrey" className="mb-2" height="55" width="55" xmlns="http://www.w3.org/2000/svg">
                                <path d="M12 22c5.421 0 10-4.579 10-10h-2c0 4.337-3.663 8-8 8s-8-3.663-8-8c0-4.336 3.663-8 8-8V2C6.579 2 2 6.58 2 12c0 5.421 4.579 10 10 10z"></path>
                            </svg>
                            No Event
                        </div>
                    </div>
                ) : (
                    <div className="result-list">

                        {results.map((item) => (
                            <div
                                className="result-card"
                                key={item._id || item.gameId}
                            >

                                {/* Event Date / Time */}
                                <div className="result-row">
                                    <div className="result-label">
                                        Event Date/Time
                                    </div>

                                    <div className="result-value">
                                        {formatDate(item.openDate || item.startDate)}
                                    </div>
                                </div>

                                {/* Event Name */}
                                <div className="result-row">
                                    <div className="result-label">
                                        EventName
                                    </div>

                                    <div className="result-value result-name">
                                        {formatName(item.name)}
                                    </div>
                                </div>

                                {/* Winner */}
                                <div className="result-row">
                                    <div className="result-label">
                                        Home
                                    </div>

                                    <div className="result-value result-winner">
                                        {item.result?.score1 || "--"}
                                    </div>
                                </div>

                                <div className="result-row">
                                    <div className="result-label">
                                        EventName
                                    </div>

                                    <div className="result-value result-winner">
                                        {item.result?.score2 || "--"}
                                    </div>
                                </div>

                            </div>
                        ))}

                    </div>
                )}
            </div>
        </>
    );
};

export default CheckResultsMobile;